/* eslint-disable react/require-default-props */
import React, { FC, Dispatch, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PersonalizationTagTypes } from '@btag/bt-personalization';

import { TAppState } from '../../../../reducers';
import { getSingleUseTagsBegin, getTag } from '../../../../actions/personalization';
import handleKeyPress from '../../../../common/handleKeyPress';
import * as Styled from './styles';
import TagList from './TagList';
import { IPersonalizationTags } from '../../../../types/personalization';
import eventService from '../../../../common/eventService';

interface IProps {
    draftID: string;
    tags: IPersonalizationTags;
    tagsInText: string[];
    isGetTagLoading: boolean;
    dispatch: Dispatch<any>;
    isGetSingleUseTagsLoading: boolean;
}

const TagPanel: FC<IProps> = ({ draftID, tags, tagsInText, isGetTagLoading, dispatch, isGetSingleUseTagsLoading }) => {
    const [menuOpen, setMenuOpen] = useState(true);
    const [singleUseTagsFiltered, setSingleUseTagsFiltered] = useState(tags.singleUse);
    const { ed563MailMergeV2 } = useFlags();

    const toggleMenu = () => {
        setMenuOpen(!menuOpen);
    };

    useEffect(() => {
        if (!isGetTagLoading) {
            dispatch(getTag({ draftID }));
        }

        if (ed563MailMergeV2 === true && !isGetSingleUseTagsLoading) {
            dispatch(getSingleUseTagsBegin());
            eventService.getSingleUseTagData();
        }
    }, []);

    useEffect(() => {
        const localSingleUseTags = { ...tags?.singleUse };
        // Need to remove firstName and lastName from singleUse to avoid duplication
        // However, we still need them in the original state to use the Preview Recipient Data
        if (localSingleUseTags.firstName) delete localSingleUseTags.firstName;
        if (localSingleUseTags.lastName) delete localSingleUseTags.lastName;
        setSingleUseTagsFiltered(localSingleUseTags);
    }, [tags]);

    // if there's no tags in text, do not render tag panel
    if (tagsInText.length === 0) {
        return null;
    }

    return (
        <Styled.TagPanelWrapper role="menuitem" aria-expanded={menuOpen} data-test-id="tag-panel-div">
            <Styled.Label
                onClick={toggleMenu}
                role="button"
                data-test-id="tag-panel-label"
                aria-label={`${menuOpen ? 'Collapse' : 'Expand'} tag fallbacks menu`}
                tabIndex={0}
                onKeyPress={(e) => handleKeyPress(e, toggleMenu)}
            >
                TAG FALLBACKS
                <i className="material-icons" aria-hidden style={{ fontSize: 18, marginTop: 2 }}>
                    {`arrow_drop_${menuOpen ? 'up' : 'down'}`}
                </i>
            </Styled.Label>
            {isGetTagLoading ? (
                <Styled.LoadingImg
                    data-test-id="tag-edit-loading-spinner"
                    width="48"
                    src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/banana-loader.gif"
                    alt="loader"
                />
            ) : (
                <>
                    {menuOpen && (
                        <>
                            <TagList
                                type={PersonalizationTagTypes.DEFAULT}
                                tagsInText={tagsInText}
                                tags={tags.default}
                            />
                            {ed563MailMergeV2 && (
                                <TagList
                                    type={PersonalizationTagTypes.ONETIME}
                                    tagsInText={tagsInText}
                                    tags={singleUseTagsFiltered}
                                />
                            )}
                        </>
                    )}
                </>
            )}
        </Styled.TagPanelWrapper>
    );
};

const mapStateToProps = (state: TAppState) => ({
    draftID: _get(state, 'session.collaboration.id', ''),
    tags: _get(state, 'personalization.tag.tags', {}),
    isGetTagLoading: _get(state, 'personalization.tag.isGetTagLoading', true),
    isGetSingleUseTagsLoading: _get(state, 'personalization.tag.isGetSingleUseTagsLoading', true),
});

export default connect(mapStateToProps)(TagPanel);
